import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:     <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:    <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    Handshake:     <><path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"/></>,
    Building:      <><rect width="16" height="20" x="4" y="2" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01M12 14h.01"/></>,
    CheckCircle:   <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    Users:         <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    Zap:           <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    Shield:        <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    BarChart:      <><line x1="12" x2="12" y1="20" y2="10"/><line x1="18" x2="18" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="16"/></>,
    Clock:         <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    Mail:          <><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>,
    Phone:         <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>,
    Briefcase:     <><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
    Home:          <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    Sparkles:      <><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3z"/></>,
    CircleCheck:   <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    MapPin:        <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Award:         <><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Benefit card ── */
function BenefitCard({ icon, title, desc }) {
  return (
    <div style={{
      padding: "24px", borderRadius: 16,
      border: `1px solid ${T.n150}`, background: T.white,
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: T.g50, border: `1.5px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name={icon} size={20} color={T.g500} strokeWidth={1.6} />
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.n900, marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 14, color: T.n600, lineHeight: 1.7 }}>{desc}</div>
      </div>
    </div>
  );
}

/* ── Use case row ── */
function UseCaseRow({ icon, title, desc, examples }) {
  return (
    <div style={{ display: "flex", gap: 18, padding: "24px 0", borderBottom: `1px solid ${T.n100}` }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: T.g50, border: `1.5px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
        <Icon name={icon} size={19} color={T.g500} strokeWidth={1.6} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.n900, marginBottom: 6 }}>{title}</div>
        <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.75, margin: "0 0 12px" }}>{desc}</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
          {examples.map((e, i) => (
            <span key={i} style={{ fontSize: 12, color: T.g700, background: T.g50, border: `1px solid ${T.g100}`, borderRadius: 99, padding: "3px 11px", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 5 }}>
              <Icon name="CheckCircle" size={11} color={T.g500} strokeWidth={2.2} />
              {e}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Process step ── */
function ProcessStep({ num, title, desc }) {
  return (
    <div style={{ display: "flex", gap: 18, padding: "20px 0", borderBottom: `1px solid ${T.n100}` }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: T.g500, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.white, fontFamily: T.sans }}>{num}</span>
      </div>
      <div style={{ flex: 1, paddingTop: 2 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.n900, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 14, color: T.n600, lineHeight: 1.65 }}>{desc}</div>
      </div>
    </div>
  );
}

/* ── Input component ── */
function Field({ label, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: T.n700 }}>
        {label}{required && <span style={{ color: "#e53e3e", marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = (focused) => ({
  width: "100%", padding: "12px 14px", boxSizing: "border-box",
  border: `1.5px solid ${focused ? T.g500 : T.n150}`, borderRadius: 10,
  fontSize: 14, fontFamily: T.sans, color: T.n900,
  background: T.white, outline: "none", transition: "border-color .15s",
});

/* ── Enquiry form ── */
function EnquiryForm() {
  const [form, setForm] = useState({
    org_name: "", contact_name: "", email: "", phone: "",
    org_type: "", team_size: "", services_needed: "", locations: "",
    frequency: "", message: "",
  });
  const [focused, setFocused]   = useState({});
  const [loading, setLoading]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]       = useState("");

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const focus = (key) => setFocused(f => ({ ...f, [key]: true }));
  const blur  = (key) => setFocused(f => ({ ...f, [key]: false }));

  const canSubmit = form.org_name.trim() && form.contact_name.trim() &&
    form.email.trim() && form.org_type && !loading;

  const handleSubmit = async () => {
    setError("");
    if (!canSubmit) return;
    setLoading(true);
    const { error: err } = await supabase.from("partnership_enquiries").insert({
      org_name:        form.org_name.trim(),
      contact_name:    form.contact_name.trim(),
      email:           form.email.trim(),
      phone:           form.phone.trim() || null,
      org_type:        form.org_type,
      team_size:       form.team_size || null,
      services_needed: form.services_needed.trim() || null,
      locations:       form.locations.trim() || null,
      frequency:       form.frequency || null,
      message:         form.message.trim() || null,
    });
    setLoading(false);
    if (err) { setError("Something went wrong. Please try again or email us directly."); return; }
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div style={{ textAlign: "center", padding: "48px 24px" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: T.g50, border: `1.5px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <Icon name="CircleCheck" size={28} color={T.g500} strokeWidth={1.6} />
        </div>
        <h3 style={{ fontFamily: T.serif, fontSize: "clamp(22px,4vw,28px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.02em" }}>
          Enquiry <em style={{ fontStyle: "italic", color: T.g600 }}>received</em>
        </h3>
        <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.75, maxWidth: 380, margin: "0 auto" }}>
          Thank you for getting in touch. Our partnerships team will review your enquiry and
          reach out within 2 business days to discuss how we can work together.
        </p>
      </div>
    );
  }

  const SELECT_STYLE = {
    width: "100%", padding: "12px 14px", boxSizing: "border-box",
    border: `1.5px solid ${T.n150}`, borderRadius: 10,
    fontSize: 14, fontFamily: T.sans, color: T.n700,
    background: T.white, outline: "none", appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238a93a8' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center",
    cursor: "pointer",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Row 1 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
        <Field label="Organisation name" required>
          <input value={form.org_name} onChange={e => set("org_name", e.target.value)}
            placeholder="Acme Properties Ltd"
            style={inputStyle(focused.org_name)}
            onFocus={() => focus("org_name")} onBlur={() => blur("org_name")} />
        </Field>
        <Field label="Your name" required>
          <input value={form.contact_name} onChange={e => set("contact_name", e.target.value)}
            placeholder="Jane Njoroge"
            style={inputStyle(focused.contact_name)}
            onFocus={() => focus("contact_name")} onBlur={() => blur("contact_name")} />
        </Field>
      </div>

      {/* Row 2 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
        <Field label="Work email" required>
          <input type="email" value={form.email} onChange={e => set("email", e.target.value)}
            placeholder="jane@acme.co.ke"
            style={inputStyle(focused.email)}
            onFocus={() => focus("email")} onBlur={() => blur("email")} />
        </Field>
        <Field label="Phone number">
          <input type="tel" value={form.phone} onChange={e => set("phone", e.target.value)}
            placeholder="+254 7XX XXX XXX"
            style={inputStyle(focused.phone)}
            onFocus={() => focus("phone")} onBlur={() => blur("phone")} />
        </Field>
      </div>

      {/* Row 3 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
        <Field label="Organisation type" required>
          <select value={form.org_type} onChange={e => set("org_type", e.target.value)} style={SELECT_STYLE}
            onFocus={e => (e.target.style.borderColor = T.g500)}
            onBlur={e => (e.target.style.borderColor = T.n150)}>
            <option value="">Select type</option>
            <option value="property_management">Property management company</option>
            <option value="real_estate">Real estate developer</option>
            <option value="corporate_offices">Corporate offices</option>
            <option value="hospitality">Hotel or hospitality</option>
            <option value="school_institution">School or institution</option>
            <option value="ngo">NGO or non-profit</option>
            <option value="government">Government or public sector</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Team or property size">
          <select value={form.team_size} onChange={e => set("team_size", e.target.value)} style={SELECT_STYLE}
            onFocus={e => (e.target.style.borderColor = T.g500)}
            onBlur={e => (e.target.style.borderColor = T.n150)}>
            <option value="">Select size</option>
            <option value="1_10">1 to 10 units or staff</option>
            <option value="11_50">11 to 50</option>
            <option value="51_200">51 to 200</option>
            <option value="200+">More than 200</option>
          </select>
        </Field>
      </div>

      {/* Row 4 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
        <Field label="Services you need">
          <input value={form.services_needed} onChange={e => set("services_needed", e.target.value)}
            placeholder="e.g. Cleaning, plumbing, electrical"
            style={inputStyle(focused.services_needed)}
            onFocus={() => focus("services_needed")} onBlur={() => blur("services_needed")} />
        </Field>
        <Field label="Locations or areas">
          <input value={form.locations} onChange={e => set("locations", e.target.value)}
            placeholder="e.g. Westlands, Karen, Kilimani"
            style={inputStyle(focused.locations)}
            onFocus={() => focus("locations")} onBlur={() => blur("locations")} />
        </Field>
      </div>

      {/* Row 5 */}
      <Field label="Booking frequency">
        <select value={form.frequency} onChange={e => set("frequency", e.target.value)} style={{ ...SELECT_STYLE, maxWidth: 320 }}
          onFocus={e => (e.target.style.borderColor = T.g500)}
          onBlur={e => (e.target.style.borderColor = T.n150)}>
          <option value="">How often do you need services?</option>
          <option value="one_off">One-off or as needed</option>
          <option value="weekly">Weekly</option>
          <option value="biweekly">Every two weeks</option>
          <option value="monthly">Monthly</option>
          <option value="multiple_weekly">Multiple times per week</option>
        </select>
      </Field>

      {/* Message */}
      <Field label="Anything else you'd like us to know">
        <textarea value={form.message} onChange={e => set("message", e.target.value.slice(0, 800))}
          placeholder="Tell us about your specific needs, any existing service arrangements, or questions you have about a partnership."
          rows={4}
          style={{ ...inputStyle(focused.message), resize: "vertical", lineHeight: 1.6 }}
          onFocus={() => focus("message")} onBlur={() => blur("message")} />
        <span style={{ fontSize: 12, color: T.n400, textAlign: "right" }}>{form.message.length} / 800</span>
      </Field>

      {error && (
        <p style={{ fontSize: 13, color: "#c0392b", margin: 0, padding: "10px 14px", background: "#fdecea", borderRadius: 8 }}>{error}</p>
      )}

      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        style={{
          padding: "14px 0", background: canSubmit ? T.g500 : T.n150,
          color: canSubmit ? T.white : T.n400,
          border: "none", borderRadius: 12, fontSize: 15, fontWeight: 600,
          fontFamily: T.sans, cursor: canSubmit ? "pointer" : "not-allowed",
          letterSpacing: "0.01em", transition: "background .15s",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}
        onMouseEnter={e => { if (canSubmit) e.currentTarget.style.background = T.g600; }}
        onMouseLeave={e => { if (canSubmit) e.currentTarget.style.background = T.g500; }}>
        {loading ? "Submitting..." : <>Send enquiry <Icon name="ArrowRight" size={16} color={canSubmit ? T.white : T.n400} strokeWidth={2} /></>}
      </button>

      <p style={{ fontSize: 12, color: T.n400, textAlign: "center", margin: 0, lineHeight: 1.6 }}>
        We respond to all partnership enquiries within 2 business days. For urgent matters,
        email us directly at{" "}
        <a href="mailto:partnerships@jiraniapp.com" style={{ color: T.g600, textDecoration: "none", borderBottom: `1px solid ${T.g200}` }}>
          partnerships@jiraniapp.com
        </a>.
      </p>
    </div>
  );
}

/* ══ Main page ══ */
export default function PartnershipsPage() {
  useFonts();
  const navigate = useNavigate();

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`* { box-sizing: border-box; } select { font-family: ${T.sans}; }`}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.n100}` }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => (e.currentTarget.style.color = T.n800)}
          onMouseLeave={e => (e.currentTarget.style.color = T.n400)}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
      </div>

      {/* ── Hero ── */}
      <div style={{ padding: "clamp(48px,7vw,88px) 20px clamp(40px,5vw,60px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 20 }}>
            <Icon name="Handshake" size={14} color={T.g500} strokeWidth={2} />
            Corporate partnerships
          </div>
          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(38px,7vw,62px)", fontWeight: 400, margin: "0 0 18px", lineHeight: 1.07, color: T.n900, letterSpacing: "-0.03em", maxWidth: 600 }}>
            Reliable home services,{" "}
            <em style={{ fontStyle: "italic", color: T.g600 }}>built for business</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 36px", maxWidth: 540 }}>
            JiraniApp partners with property managers, corporate offices, hospitality businesses,
            and institutions that need consistent, verified home services at scale. One platform,
            one point of contact, and professionals you can rely on every time.
          </p>
          <a href="#enquiry-form" style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "14px 28px", background: T.g500, color: T.white,
            border: "none", borderRadius: 12, fontSize: 15, fontWeight: 600,
            fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
            textDecoration: "none",
          }}
          onMouseEnter={e => (e.currentTarget.style.background = T.g600)}
          onMouseLeave={e => (e.currentTarget.style.background = T.g500)}>
            Get in touch
            <Icon name="ArrowRight" size={16} color={T.white} strokeWidth={2} />
          </a>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px clamp(80px,10vw,120px)" }}>

        {/* ── Stats ── */}
        <div style={{ paddingTop: "clamp(36px,5vw,52px)", display: "flex", flexWrap: "wrap", gap: 12, marginBottom: "clamp(48px,7vw,72px)" }}>
          {[
            { num: "700+",  label: "Verified professionals" },
            { num: "4",     label: "Cities active" },
            { num: "40+",   label: "Service categories" },
            { num: "48h",   label: "Dispute resolution target" },
          ].map(s => (
            <div key={s.label} style={{ flex: "1 1 130px", padding: "18px 20px", borderRadius: 14, border: `1px solid ${T.n150}`, background: T.n50 }}>
              <div style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,30px)", fontWeight: 400, color: T.n900, letterSpacing: "-0.03em", lineHeight: 1, marginBottom: 5 }}>{s.num}</div>
              <div style={{ fontSize: 13, color: T.n400 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Who this is for ── */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>Who we work with</span>
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Built for organisations that need{" "}
            <em style={{ fontStyle: "italic", color: T.g600 }}>consistency</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            Whether you manage one building or fifty, JiraniApp gives you a single reliable
            layer for all your home service needs.
          </p>
          <div>
            <UseCaseRow
              icon="Building"
              title="Property management companies"
              desc="Manage maintenance, cleaning, and repairs across multiple properties from one account. Assign jobs to specific units, track completion in real time, and maintain a full record of every booking and payment across your portfolio."
              examples={["Multi-property management", "Unit-level job tracking", "Centralised invoicing", "Recurring schedules"]}
            />
            <UseCaseRow
              icon="Briefcase"
              title="Corporate offices and facilities teams"
              desc="Keep your office running without the operational overhead of managing individual contractors. Book cleaning, electrical maintenance, plumbing, and other services with verified professionals on a schedule that fits your business."
              examples={["Regular office cleaning", "Maintenance scheduling", "Emergency call-outs", "Verified professionals"]}
            />
            <UseCaseRow
              icon="Home"
              title="Real estate developers"
              desc="From post-construction cleaning to ongoing maintenance for completed developments, JiraniApp gives real estate teams a reliable pool of vetted professionals available across Nairobi, Mombasa, Kisumu, and Nakuru."
              examples={["Post-construction cleaning", "Handover preparation", "Ongoing maintenance", "Multi-site coverage"]}
            />
            <UseCaseRow
              icon="Users"
              title="Hospitality and accommodation"
              desc="Hotels, serviced apartments, and short-stay properties need reliable, fast turnaround on cleaning and maintenance. JiraniApp gives hospitality teams access to professional services on demand, with full booking records and accountability."
              examples={["Turnover cleaning", "Same-day availability", "On-demand repairs", "Quality accountability"]}
            />
            <UseCaseRow
              icon="Award"
              title="Schools and institutions"
              desc="Educational institutions and NGOs often need regular cleaning, maintenance, and repair services without the complexity of in-house staffing. JiraniApp provides flexible, scalable access to professionals across all service types."
              examples={["Regular cleaning", "Maintenance contracts", "Flexible scheduling", "Documented records"]}
            />
          </div>
        </div>

        {/* ── What partners get ── */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>Partnership benefits</span>
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            What corporate partners{" "}
            <em style={{ fontStyle: "italic", color: T.g600 }}>receive</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 24px", lineHeight: 1.65 }}>
            More than access to professionals. A full operational layer built around your needs.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
            <BenefitCard icon="Users" title="Dedicated account manager" desc="A single point of contact who understands your organisation, your locations, and your service requirements." />
            <BenefitCard icon="BarChart" title="Consolidated billing and reporting" desc="Monthly invoicing across all bookings in one document. Full records of jobs, professionals, and payments available on demand." />
            <BenefitCard icon="Zap" title="Priority booking access" desc="Corporate partners are prioritised when scheduling professionals, particularly for recurring bookings and last-minute needs." />
            <BenefitCard icon="Shield" title="Full Jirani Guarantee coverage" desc="Every booking is backed by our Trust and Safety team, dispute resolution process, and Jirani Guarantee, at every scale." />
            <BenefitCard icon="Clock" title="Flexible recurring schedules" desc="Set up recurring weekly, fortnightly, or monthly bookings across one or multiple locations, managed from a single dashboard." />
            <BenefitCard icon="MapPin" title="Multi-location coverage" desc="Active across Nairobi, Mombasa, Kisumu, and Nakuru, with a consistent standard of verification and service quality in each city." />
          </div>
        </div>

        {/* ── How it works for partners ── */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>The process</span>
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            How a partnership{" "}
            <em style={{ fontStyle: "italic", color: T.g600 }}>works</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            From first contact to fully operational in under a week.
          </p>
          <div>
            <ProcessStep num="01" title="Submit your enquiry" desc="Fill in the form below with your organisation's details, service needs, and locations. Our partnerships team reviews every submission personally." />
            <ProcessStep num="02" title="Discovery call" desc="We get on a call to understand your requirements in detail, including frequency, specific service types, number of locations, and any particular standards you need met." />
            <ProcessStep num="03" title="Custom setup" desc="We configure your account with your preferred settings, assign a dedicated account manager, and pre-qualify a pool of professionals suited to your needs." />
            <ProcessStep num="04" title="Onboarding and first bookings" desc="Your team is walked through the platform, recurring schedules are set up, and your first bookings are placed under the partnership arrangement." />
            <ProcessStep num="05" title="Ongoing management" desc="Your account manager stays in contact for scheduling, billing queries, and any service issues. Quarterly reviews available for larger partners." />
          </div>
        </div>

        {/* ── Enquiry form ── */}
        <div id="enquiry-form" style={{ scrollMarginTop: 32 }}>
          <div style={{ marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase" }}>Get in touch</span>
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,4vw,32px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.03em", lineHeight: 1.2 }}>
            Start a{" "}
            <em style={{ fontStyle: "italic", color: T.g600 }}>partnership</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            Tell us about your organisation and what you need. We will follow up within 2 business days.
          </p>
          <div style={{ background: T.white, borderRadius: 20, border: `1px solid ${T.n150}`, padding: "clamp(24px,5vw,40px)", boxShadow: "0 4px 24px rgba(15,17,23,.04)" }}>
            <EnquiryForm />
          </div>
        </div>

        {/* ── Bottom CTA ── */}
        <div style={{ marginTop: "clamp(48px,7vw,72px)", padding: "clamp(28px,5vw,40px)", borderRadius: 20, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h3 style={{ fontFamily: T.serif, fontSize: "clamp(20px,3vw,26px)", fontWeight: 400, color: T.n900, margin: "0 0 6px", letterSpacing: "-0.02em" }}>
              Questions before you{" "}
              <em style={{ fontStyle: "italic", color: T.g600 }}>enquire?</em>
            </h3>
            <p style={{ fontSize: 14, color: T.n600, margin: 0, lineHeight: 1.65 }}>
              Visit our Help and Support page or email us directly.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <a href="mailto:partnerships@jiraniapp.com" style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "11px 20px", background: T.white, color: T.n700,
              border: `1.5px solid ${T.n200}`, borderRadius: 10,
              textDecoration: "none", fontSize: 13, fontWeight: 600, fontFamily: T.sans,
            }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = T.n400)}
            onMouseLeave={e => (e.currentTarget.style.borderColor = T.n200)}>
              <Icon name="Mail" size={14} strokeWidth={1.8} />
              Email us
            </a>
            <button onClick={() => navigate("/help")} style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "11px 20px", background: T.g500, color: T.white,
              border: "none", borderRadius: 10,
              fontSize: 13, fontWeight: 600, fontFamily: T.sans, cursor: "pointer",
            }}
            onMouseEnter={e => (e.currentTarget.style.background = T.g600)}
            onMouseLeave={e => (e.currentTarget.style.background = T.g500)}>
              Help and Support
              <Icon name="ArrowRight" size={14} color={T.white} strokeWidth={2} />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}